import { useEffect, useRef } from 'react';
import katex from 'katex';
import 'katex/dist/katex.min.css';

interface MathRendererProps {
  math: string;
  displayMode?: boolean;
  className?: string;
}

export function MathRenderer({ math, displayMode = false, className = '' }: MathRendererProps) {
  const elementRef = useRef<HTMLSpanElement>(null);

  useEffect(() => {
    if (elementRef.current && math) {
      try {
        katex.render(math, elementRef.current, {
          displayMode,
          throwOnError: false,
          strict: false,
        });
      } catch (error) {
        console.warn('KaTeX rendering error:', error);
        // Fallback to plain text if rendering fails
        if (elementRef.current) {
          elementRef.current.textContent = math;
        }
      }
    }
  }, [math, displayMode]);

  // If no math content, render nothing
  if (!math || math.trim() === '') {
    return null;
  }

  return (
    <span 
      ref={elementRef} 
      className={className}
      data-testid="math-renderer"
    />
  );
}

// Component for inline math expressions
export function InlineMath({ math, className }: { math: string; className?: string }) {
  return <MathRenderer math={math} displayMode={false} className={className} />;
}

// Component for block-level math expressions
export function DisplayMath({ math, className }: { math: string; className?: string }) {
  return <MathRenderer math={math} displayMode={true} className={className} />;
}